/**
 * Bookingregisteret — artistene, byråene, utkastene og budene (Atle, 3. september).
 *
 * ---------------------------------------------------------------------------------------------
 * DETTE ER IKKE `booking.ts`, OG DE TO SKAL IKKE SLÅS SAMMEN.
 *
 * `booking.ts` svarer på «hva har vi gjort med dem og hva har vi betalt» — én rad per KONTAKT,
 * hvert år brettet inn, alt hentet fra programposter som allerede finnes. Denne fila svarer på
 * «hvem kunne vi tenke oss», og radene her er artister ingen har snakket med ennå. Den ene leser
 * historikk, den andre skriver spekulasjon.
 *
 * ---------------------------------------------------------------------------------------------
 * EN ARTIST ER IKKE EN KONTAKT. AGENTEN ER DET.
 *
 * Atle: «Når vi leker med artistene inne i booking vil vi ikke forhøre oss med andre enn
 * bookingagenten, som blir kontakten. […] Det er ingen nødvendighet å ha kontakt på en artist før
 * advance begynner.»
 *
 * Derfor har `BookingRegisterArtistDto` ingen `contactId`, mens `BookingAgencyDto` har den. Det
 * går motsatt vei av hva man gjetter, og det er grunnen til at det står her og i skjemaet.
 *
 * ---------------------------------------------------------------------------------------------
 * ALLE BELØP ER HELTALL AV MINSTE ENHET, ALDRI EN STRENG OG ALDRI ET DESIMALTALL.
 *
 * 80 000 kr er `8000000`. Formateringen hører klienten og `Intl.NumberFormat` til, som kjenner
 * leserens locale. Det er samme regel som `economy.ts` — og `MAX_COST_AMOUNT_MINOR` importeres
 * derfra i stedet for å skrives på nytt, fordi taket er den samme vakten mot en tastefeil.
 *
 * `totalMinor` REGNES AV APIET OG LAGRES ALDRI. De fire beløpene er hver sin beslutning — en
 * artist til 80 000 som må flys inn fra Berlin er ikke den samme som en til 80 000 som bor i byen —
 * og en lagret sum er den kolonnen som blir feil den dagen noen retter ett av de fire.
 *
 * ---------------------------------------------------------------------------------------------
 * ESTIMATENE OG BOOKING SCORE ER BORTE (0999, sak 272). Tvitvi viser bare det brukerne legger inn
 * selv eller det som kommer fra Soundcharts. Honoraret og antallet som reiser kan være tomme.
 */
import { z } from 'zod';
import { engagementRefDtoSchema } from './engagements.js';
import { dealTermsInputSchema } from '../deal/deal-terms.js';

import {
  BOOKING_ARTIST_STATUSES,
  BOOKING_GENDERS,
  bookingArtistStatusSchema,
  bookingBidKindSchema,
  bookingBidStatusSchema,
  bookingGenderSchema,
  performanceStatusSchema,
} from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { urlInputSchema } from '../url.js';

import { bookingContactSchema } from './agency-moves.js';
import { enquiryAvailabilitySchema } from './booking-enquiries.js';

import { bidPaymentSchema, bidTermsInputSchema, bidTermsSchema } from './booking-terms.js';
import { MAX_COST_AMOUNT_MINOR } from './economy.js';
import { registerSoundchartsStatsSchema } from './soundcharts.js';
import { CONCERT_DEAL_TYPES } from '../concerts/deal-math.js';
import { VENUE_NIGHT_STATUSES } from './venues.js';

// ------------------------------------------------------------------ grenser

/** Et navn på en artist eller et byrå. Langt nok for «Aurora Kollektivet feat. Havbris». */
export const MAX_BOOKING_NAME = 160;

/** Sjanger, type og hjemsted — ett ord eller to, aldri en beskrivelse. */
export const MAX_BOOKING_LABEL = 60;

/** Et notat. Én skjerm, ikke et dokument: det er Dokumenter til for. */
export const MAX_BOOKING_NOTE = 2000;

/** Byråets svar, ført inn av den som fikk telefonen. */
export const MAX_BID_RESPONSE_NOTE = 1000;

/** Hvor mange som kommer. Brukt til reise og mat, ikke til honoraret. */
export const MAX_PARTY_SIZE = 200;

/**
 * Hvor mange byråer én organisasjon holder.
 *
 * Lista leses hel og alfabetisk — den er en oppslagsliste bak et felt, ikke en side man blar i.
 * Et festivalregister med over to hundre byråer er et register som trenger sin egen side, og da
 * er det den avgjørelsen som skal tas, ikke dette tallet som skal heves i stillhet.
 */
export const MAX_BOOKING_AGENCIES = 200;

/** Navnet på et utkast: «Utkast A», «Med Kvitbjørn på lørdag». */
export const MAX_DRAFT_NAME = 80;

/** Meldingen til byrået. Én skjerm, ikke et dokument — lengre enn dette er et vedlegg. */
export const MAX_BID_MESSAGE = 4000;

// ------------------------------------------------------------------ byrå

export const bookingAgencyDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    websiteUrl: z.string().nullable(),
    /** Siden byråskanningen leser artistene fra (sak 243). Null: skanningen finner lista selv. */
    artistPageUrl: z.string().nullable(),
    /** Agenten. DETTE er kontakten — ikke artisten. Se filhodet. */
    contactId: z.uuid().nullable(),
    /** Oppslått for visning, så lista slipper å slå opp i kontaktregisteret selv. */
    contactName: z.string().nullable(),
    notes: z.string().nullable(),
    /** Hvor mange av registerets artister som ligger hos dem. */
    artistCount: z.number().int().nonnegative(),
    /**
     * HVEM SOM EIER RADEN (K-8, migrering 0270).
     *
     * `own` — huset la byrået inn selv. Blyant og søppelkasse står.
     * `register` — innhentingen eller plattformen eier det. «Riktig info kommer fra AI-scriptet
     * eller fra byrået selv», så skjermen viser ingen blyant og serveren avviser PATCH og DELETE.
     * Feltet er her fordi UI-et skal kunne LA VÆRE å tilby noe serveren uansett nekter.
     */
    origin: z.enum(['own', 'register']),
    /**
     * Om byrået er et Tvitvi-hus (0251). Merket «I Tvitvi» i popupen, og svaret på #25c.
     *
     * Id-en følger ikke med: hvilket hus det er, er ikke festivalens sak, og den ville vært en
     * peker inn i et annet hus i en DTO som leses av alle med `performance:list`.
     */
    inTvitvi: z.boolean(),
    /** Om DENNE brukeren har skjult byrået (#25). Per menneske, aldri per hus. */
    hidden: z.boolean(),
  })
  .meta({ id: 'BookingAgencyDto' });
export type BookingAgencyDto = z.infer<typeof bookingAgencyDtoSchema>;

/** Ikke paget: `MAX_BOOKING_AGENCIES` avgrenser den, og en oppslagsliste leses hel. */
export const bookingAgencyListResponseSchema = z
  .object({
    items: z.array(bookingAgencyDtoSchema),
    /**
     * Hvor mange byråer DENNE brukeren har skjult (#25, «Vis skjulte (n)»).
     *
     * Et tall og ikke en beregning i klienten: lista over synlige byråer inneholder per definisjon
     * ikke de skjulte, så klienten kan ikke telle dem. Uten tallet er skjulingen en handling man
     * ikke kan angre, fordi man ikke vet at det finnes noe å angre.
     */
    hiddenCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'BookingAgencyListResponse' });
export type BookingAgencyListResponse = z.infer<typeof bookingAgencyListResponseSchema>;

/** `?includeHidden=true` — «Vis skjulte (n)». Uten den er de skjulte ute av lista. */
export const listBookingAgenciesQuerySchema = z
  .object({
    includeHidden: z
      .preprocess((value) => (value === 'true' || value === true ? true : undefined), z.boolean())
      .optional(),
  })
  .meta({ id: 'ListBookingAgenciesQuery' });
export type ListBookingAgenciesQuery = z.infer<typeof listBookingAgenciesQuerySchema>;

/** «Skjul byrået for meg» / «vis det igjen». Per menneske; se DTO-ens `hidden`. */
export const setBookingAgencyHiddenRequestSchema = z
  .object({ hidden: z.boolean() })
  .meta({ id: 'SetBookingAgencyHiddenRequest' });
export type SetBookingAgencyHiddenRequest = z.infer<typeof setBookingAgencyHiddenRequestSchema>;

export const createBookingAgencyRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_BOOKING_NAME),
    websiteUrl: urlInputSchema(500).nullish(),
    artistPageUrl: urlInputSchema(2048).nullish(),
    contactId: z.uuid().nullish(),
    notes: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
  })
  .meta({ id: 'CreateBookingAgencyRequest' });
export type CreateBookingAgencyRequest = z.infer<typeof createBookingAgencyRequestSchema>;

export const updateBookingAgencyRequestSchema = createBookingAgencyRequestSchema
  .partial()
  .meta({ id: 'UpdateBookingAgencyRequest' });
export type UpdateBookingAgencyRequest = z.infer<typeof updateBookingAgencyRequestSchema>;

// ------------------------------------------------------------------ artist

/**
 * «ARTIST DEKKER: REISE / OVERNATTING / MAT» (0440, Atle 29. september). Et avkrysset ledd er
 * inkludert i honoraret: beløpet står fortsatt på artisten, men teller som 0 i totalen.
 */
export const artistCoversSchema = z.object({
  travel: z.boolean(),
  accommodation: z.boolean(),
  food: z.boolean(),
});
export type ArtistCovers = z.infer<typeof artistCoversSchema>;

export const bookingRegisterArtistDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    agencyId: z.uuid().nullable(),
    /** Kontaktpersonen for artisten (migrering 0102), og navnet hennes for skjermen. */
    contactId: z.uuid().nullable(),
    contactName: z.string().nullable(),
    /** Oppslått for visning. En artist uten byrå er lovlig — man har sett dem, ikke funnet dem. */
    agencyName: z.string().nullable(),
    /**
     * Om byrået er i Tvitvi med en akseptert kobling (QA H5).
     *
     * Skjermene bruker den til å slutte å si «Tvitvi sender ikke til byrået ennå» på en
     * forespørsel som faktisk havner i innboksen deres med det samme.
     */
    agencyInTvitvi: z.boolean(),

    genre: z.string().nullable(),
    artistType: z.string().nullable(),
    homeTown: z.string().nullable(),

    /**
     * STØRRELSENE ARTISTEN KAN BOOKES I, som navn (0110, ARTISTS.md §1).
     *
     * -----------------------------------------------------------------------------------------
     * Atle, 10. september: «New `Formats` column showing the format names as flat pills. An asking
     * fee only means something next to the format it belongs to.»
     *
     * BARE NAVNENE, og ikke hele formatobjektene. Lista er 741 rader lang, og hvert format bærer
     * pris, beskrivelse og et medlemstall som ingen av dem kan vises i en celle. Å sende dem ville
     * mangedoblet svaret for å tegne to piller. Den som vil ha tallene åpner artisten.
     */
    formatNames: z.array(z.string()),
    /**
     * STANDARDFORMATETS HONORAR (sak 108.1, QA-punkt 34).
     *
     * `feeMinor` er artistens EGET felt, og det er det `totalMinor` summerer. Dette er tallet
     * §5.2 sier eier prisen — og de to er med vilje ikke det samme feltet: en artist med tre
     * størrelser HAR ikke ett honorar, mens summen må kunne legges sammen.
     *
     * Null når artisten ikke har et standardformat. Da er `feeMinor` alt som finnes.
     */
    defaultFormatFeeMinor: z.number().int().nullable(),
    /**
     * STANDARDFORMATETS ID (OVERSIKT §4 #2, bølge 3 H) — det Honorar-cella i hurtigredigeringen
     * skriver til (`PATCH /booking/roster/formats/:formatId`). Null når artisten ikke har et
     * standardformat. Valgfri i kontrakten fordi et API som ikke er startet på nytt ennå ikke sender
     * den; da er cella grå i stedet for at hele registeret feiler parsingen.
     */
    defaultFormatId: z.uuid().nullable().optional(),
    /*
     * `lastAskingFeeMinor` STO HER TIL QA14 #11 (vedtak 184, K-21).
     *
     * Feltet bar `booking_enquiries.asking_fee_minor` fra byråets nyeste forespørsel, og var
     * reserven under katalogprisen i HONORAR-kolonnen. Den kolonnen skrives av `answer()` i
     * `booking-enquiries.service.ts` i det byrået SVARER på et bud — så et godtatt bud på 66 000
     * kom tilbake som artistens egen asking fee, som er nøyaktig det K-13 forbyr.
     *
     * Atle, vedtak 184: honoraret er standardformatets pris og ingenting annet. Uten en leser var
     * feltet bare en vei tilbake til den samme feilen, og det er derfor det er borte framfor å stå
     * ubrukt i kontrakten.
     */

    /*
     * BELØPENE ER NULL FOR DEN SOM IKKE FÅR SE DEM (QA6 K2 funn 3, `canSeeBookingAmounts`).
     * Det gjelder hvert beløp i svaret — honorar, totaler, anslag, bekreftet pris og siste bud —
     * og et null her betyr «skjult», ikke «0 kr». For den som ser beløp er de alltid tall.
     */
    feeMinor: z.number().int().nonnegative().nullable(),
    travelMinor: z.number().int().nonnegative().nullable(),
    accommodationMinor: z.number().int().nonnegative().nullable(),
    foodMinor: z.number().int().nonnegative().nullable(),
    /**
     * «ARTIST DEKKER» (0440): linjene som er inkludert i honoraret. Beløpet over står fortsatt,
     * men `totalMinor` teller en dekket linje som 0, og skjermen skriver «Inkl. i honorar».
     *
     * STANDARDVERDIEN ER FOR ET API SOM IKKE ER STARTET PÅ NYTT ENNÅ: et svar uten feltet ville
     * ellers vært et kontraktbrudd, og hele registeret ville stått tomt i nettleseren til da.
     */
    artistCovers: artistCoversSchema.default({ travel: false, accommodation: false, food: false }),
    /** Summen av de fire, med dekkede linjer som 0. Regnet av APIET, aldri lagret — se filhodet. */
    totalMinor: z.number().int().nonnegative().nullable(),
    currency: z.string(),
    /** Antallet som reiser, satt av noen. Null til da (272.4): ingen antakelse. */
    partySize: z.number().int().positive().nullable(),
    /**
     * REISE, OPPHOLD OG MAT: FRA SATSENE ELLER SATT (0926, 0999). `rates` er husets satser × det
     * faktiske antallet (tomt antall gir 0); `set` er faktisk kostnad og regnes aldri om.
     */
    extrasSource: z.enum(['rates', 'set']).optional(),

    /**
     * DENNE ARTISTENS PROVISJONSSATS, NAAR DEN AVVIKER FRA HUSETS (vedtak 102.1, 0165).
     *
     * Null betyr BRUK HUSETS SATS, ikke null prosent. Skillet er ekte: et byraa kan ha en artist
     * de ikke tar provisjon av, og den artisten maa kunne si 0 uten at det leses som at ingen har
     * svart.
     */
    commissionBp: z.number().int().min(0).max(10000).nullable(),
    monthlyListeners: z.number().int().nonnegative().nullable(),

    status: bookingArtistStatusSchema,
    shortlisted: z.boolean(),
    /**
     * AGENTEN SOM HAR DENNE ARTISTEN (sak 118, migrering 0184).
     *
     * Atle, 13. september: «Et byrå setter en ansvarlig per ARTIST, ikke per forespørsel. Den
     * ansvarlige skal vises på artisten i bookingsiden, på samme måte som i Greenroom.»
     *
     * Navnet følger med fordi lista skal kunne tegne det uten et kall per rad — samme grep som
     * `agencyName` over. Null er en ekte tilstand: en artist ingen har tatt.
     */
    assigneePersonId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    /**
     * BOOKINGKONTAKTEN (byråskanningen, del C): agenten byrået oppgir for artisten, ellers byråets
     * generelle kontakt. Null når ingen av delene er kjent. Står i popupen som «Bookingkontakt».
     */
    bookingContact: bookingContactSchema.nullable(),
    /** «Ny – ikke priset»: lagt til av byråskanningen, og ingen har satt en pris ennå. */
    newUnpriced: z.boolean(),
    imageUrl: z.string().nullable(),
    notes: z.string().nullable(),
    /** Arkivert fra byråets stall (0390, 215.29). Skjult i stallen som standard, aldri slettet. */
    archivedAt: z.iso.datetime().nullable(),
    /**
     * SKJULT I DENNE ORGANISASJONEN (sak 237.1). En artist fra skanningen eller importen slettes ikke,
     * den skjules: raden står, så skanningen kjenner henne igjen og legger henne ikke inn på nytt.
     * Valgfri, så et API som ikke er startet på nytt ennå ikke bryter kontrakten.
     */
    hiddenAt: z.iso.datetime().nullable().optional(),

    /**
     * Om prisen er BYRÅETS SVAR eller VÅR GJETNING.
     *
     * Ikke «har et beløp» — de fire har default 0, så det er alltid sant. Dette er om noen har
     * SPURT, og det er forskjellen på «~ 93 750» i grått og «85 000» i svart.
     */
    priceConfirmed: z.boolean(),
    gender: bookingGenderSchema.nullable(),
    frontPerson: z.string().nullable(),
    /** «16–30 år». Fritekst: en festival deler publikum sitt slik den selv gjør det. */
    ageGroup: z.string().nullable(),
    spotifyUrl: z.string().nullable(),
    /**
     * Om vi vet at Spotify-profilen er RIKTIG ARTIST.
     *
     * Står på artisten og ikke bare i en jobb-logg fordi det er en opplysning bookeren trenger:
     * er den `ambiguous`, kan lyttertallet tilhøre en annen — og ved importen av greenroom-
     * registeret gjaldt det 91 av 696 artister, 50 av dem fordi flere artister delte den samme
     * profilen.
     */
    identityStatus: z.enum(['unverified', 'verified', 'ambiguous', 'not_found']),
    /**
     * HVORFOR den er tvilsom, skrevet for mennesket som skal avgjøre.
     *
     * «Karpe deler profilen sin med Aftenpodden og fire andre» er hele grunnen til at raden
     * står i køen, og uten den er «avklar identiteten» en oppgave uten et spørsmål.
     */
    identityNote: z.string().nullable(),
    /** Selve id-en, ikke bare URL-en: den er det mennesket limer inn eller fjerner. */
    spotifyId: z.string().nullable(),
    instagramHandle: z.string().nullable(),
    instagramFollowers: z.number().int().nonnegative().nullable(),
    /**
     * Soundcharts' siste tall for den offisielle artisten (B4, sak 273). Null uten offisiell artist.
     * Valgfri i typen bare så eldre fiksturer slipper feltet; API-et sender det alltid.
     */
    soundcharts: registerSoundchartsStatsSchema.nullish(),

    /**
     * PRISEN NOEN HAR SAGT, mot anslaget vi selv gjettet (0188, QA runde 9 P1 #1).
     *
     * `null` betyr at ingen har sagt noe ennå, og da er `feeMinor` det beste vi har. Er den satt,
     * er `totalMinor` regnet på DEN — og raden slutter å vise tilden, fordi tallet ikke lenger er
     * et anslag.
     */
    agreedFeeMinor: z.number().int().nullable(),

    /**
     * HVEM SOM EIER RADEN (K-6, migrering 0270).
     *
     * `register` betyr at innhentingen laget den, og at endringer huset gjør lagres som en LOKAL
     * overstyring. Kortet sier det, slik at den som retter sjangeren vet at rettelsen er husets og
     * ikke en melding til byrået.
     */
    origin: z.enum(['own', 'register']),

    /**
     * FELTENE HUSET HAR OVERSTYRT PÅ EN REGISTERARTIST, og hva kilden sier nå (K-6).
     *
     * Tom liste for en `own`-artist — der finnes ingen kilde å være uenig med.
     *
     * `sourceValue` er kildens verdi slik den sto DA vi overstyrte; `currentSourceValue` er hva
     * kilden sier i dag. Er de forskjellige, har kilden flyttet seg, og feltet får varselet
     * «Byrået sier nå X — bruk ny / behold min». Er de like, er skjermen stille.
     *
     * API-et sender begge og regner ikke ut «skal varsles» for skjermen: den ene setningen som
     * skal oversettes er skjermens, og en boolsk `changed` ville dessuten skjult HVA kilden sier.
     */
    overrides: z.array(
      z.object({
        /** Kolonnenavnet, som i basen: `genre`, `home_town`, `artist_type`, … */
        field: z.string(),
        value: z.string().nullable(),
        sourceValue: z.string().nullable(),
        currentSourceValue: z.string().nullable(),
        overriddenAt: z.iso.datetime(),
        /**
         * ÅRGANGEN OVERSTYRINGEN GJELDER FOR (225.4). Skanningen og byrået rører ikke feltet så
         * lenge årgangen huset jobber med (`workingEditionYear`) ikke er senere. Valgfri med
         * standard, så et API som ikke er startet på nytt ikke gir kontraktbrudd.
         */
        editionYear: z.number().int().nullable().default(null),
        /**
         * Hvem som sist fant en annen verdi mens overstyringen gjaldt: skanningen, byrået selv
         * (223.4) eller den offisielle lista (`official`, sak 247). Verdien står i
         * `currentSourceValue`; `null` når ingen har funnet noe.
         */
        foundSource: z.enum(['scan', 'agency_self', 'official']).nullable().default(null),
        foundAt: z.iso.datetime().nullable().default(null),
        /** Visningsnavnet for `currentSourceValue` der verdien er en id (byrået). */
        currentSourceLabel: z.string().nullable().default(null),
      }),
    ),

    /**
     * ÅRET SOM BOOKES (`app.booking_working_year`, 0441/0472): det seneste av inneværende år,
     * artistens nyeste bekreftede år og året husets prismodell regner for. «Bekreft pris» skriver
     * for dette året, og `confirmedPrice` gjelder bare det. Valgfri med standard, så et API som
     * ikke er startet på nytt ikke gir kontraktbrudd — da regner skjermen `workingEditionYear`.
     */
    workingYear: z.number().int().nullable().default(null),

    /**
     * ÉN PRIS FOR ÅRGANGEN, OG HVOR DEN KOM FRA (#27, 34a).
     *
     * Atle, 19. september: «siste BEKREFTEDE pris gjelder, med historikk og kilde. Ikke siste
     * skrevne.» Og 30. september: bare for ÅRET SOM BOOKES (`workingYear`). En bekreftelse for et
     * tidligere år er historikk — den står i prishistorikken, ikke her.
     *
     * `null` betyr at ingen har bekreftet noe for arbeidsåret, og da er honoraret et anslag: kortet
     * setter tilde foran og skriver «estimert». Er den satt, ER det honoraret — også når noen
     * senere har skrevet et annet tall i `feeMinor`, som er nettopp det «ikke siste skrevne»
     * betyr.
     *
     * Reise, opphold og mat følger ikke med: de er husets egne tall, og byrået har ikke sagt noe
     * om dem. Se `COST_MINOR` i API-et, som har hatt den samme regelen siden runde 8.
     */
    confirmedPrice: z
      .object({
        year: z.number().int(),
        feeMinor: z.number().int().nonnegative(),
        currency: z.string(),
        confirmedAt: z.iso.datetime(),
        /** `manual` | `agency` | `import` — skjermen velger setningen selv. */
        sourceKind: z.enum(['manual', 'agency', 'import']),
        /** Hvem: personen som bekreftet, eller byrået som sa det. Null når ingen av delene. */
        sourceName: z.string().nullable(),
      })
      .nullable(),

    /** Siste bud eller interesse, så lista kan si «venter svar» uten et kall per rad. */
    latestBid: z
      .object({
        id: z.uuid(),
        kind: bookingBidKindSchema,
        status: bookingBidStatusSchema,
        amountMinor: z.number().int().nullable(),
        /** Byråets egen pris fra svaret deres. Budskjemaet åpner på den (QA H3). */
        askingFeeMinor: z.number().int().nullable(),
        sentAt: z.iso.datetime().nullable(),
        respondsBy: z.iso.date().nullable(),
        /**
         * NÅR BYRÅET SVARTE, OG HVA DE SVARTE (QA runde 9, P1 #2).
         *
         * Raden sa «Kontaktet · Venter svar» også etter at byrået hadde svart LEDIG 145 000, fordi
         * merkelappen bare så på `status === 'sent'` — og budets status BLIR stående på «sendt»
         * etter et svar. Den flytter seg først når noen aksepterer.
         *
         * Statusstigen har ingen trinn mellom «kontaktet» og «bud sendt», og det skal den heller
         * ikke ha: vi HAR kontaktet dem, og det er fortsatt sant. Det som var usant var den andre
         * halvdelen av setningen. Så svaret bæres her framfor å bli en ny status.
         */
        answeredAt: z.iso.datetime().nullable(),
        availability: enquiryAvailabilitySchema.nullable(),
      })
      .nullable(),

    /**
     * «Fra den offisielle lista» (0940, 253.68): lagt i byråets stall da byrået ble koblet til lista
     * i Control Room. Byråets egen rad; merket sier bare hvor den kom fra.
     */
    fromOfficialList: z.boolean().optional(),

    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'BookingRegisterArtistDto' });
export type BookingRegisterArtistDto = z.infer<typeof bookingRegisterArtistDtoSchema>;

/**
 * De tre sorteringene lista tilbyr, og hver av dem har sin egen indeks (migrering 0065).
 *
 * `cost` sorterer på SUMMEN av de fire beløpene, som er tallet raden viser. Å sortere på `fee`
 * alene ville rangert en artist som koster 300 000 i reise over en som koster 40 000 mer i
 * honorar, og lista ville sett ut som om den ikke sorterte.
 */
/**
 * Sorteringene registeret tilbyr — én per kolonne som er verdt å ordne på.
 *
 * Atle, 8. september, med et skjermbilde av kolonneraden: «Går ikke ann å sortere på disse.» De
 * tre første var alt som fantes, mens tabellen hadde ni kolonner — og de seks andre så ut som
 * kolonner man kan trykke på, fordi alle andre tabeller i produktet lar deg det.
 *
 * Migrering 0076 la indeksene bak de tre nye som er kolonner på artisten. `agency` sorterer på et
 * joinet navn og har ingen indeks; se `ARTIST_SORTS` for målingen.
 */
/**
 * Mennesket avgjør identiteten (sak 89 sin andre halvdel).
 *
 * ---------------------------------------------------------------------------------------------
 * Ved importen av greenroom-registeret sto 91 av 696 artister uten en identitet vi kunne stole
 * på — 50 av dem fordi flere artister delte den samme Spotify-profilen. `ambiguous` var riktig
 * svar den dagen, og det er fortsatt riktig svar: en smartere gjetning ville bare flyttet feilen.
 *
 * Det som manglet var VEIEN UT. Denne skrivingen er den: et menneske ser navnet, notatet og
 * profilen, og sier enten «ja, det er henne» eller «nei, vi fant henne ikke».
 *
 * BARE TO UTFALL, og det er med vilje. `unverified` er tilstanden en rad har FØR noen har sett
 * på den, og å kunne sette den tilbake ville latt en avklaring bli uavklart uten at noen ser
 * forskjell på «ikke sjekket» og «sjekket, og vi vet ikke».
 */
export const IDENTITY_RESOLUTIONS = ['verified', 'not_found'] as const;
export type IdentityResolution = (typeof IDENTITY_RESOLUTIONS)[number];

export const resolveArtistIdentityRequestSchema = z
  .object({
    resolution: z.enum(IDENTITY_RESOLUTIONS),
    /**
     * Spotify-id-en mennesket bekrefter. Påkrevd for `verified` og forbudt for `not_found` —
     * en bekreftet artist uten en id er en bekreftelse på ingenting.
     */
    spotifyId: z.string().trim().min(1).max(64).nullish(),
    /** Hva mennesket så. Blir stående i `identity_note` framfor å slette begrunnelsen. */
    note: z.string().trim().max(2000).nullish(),
  })
  .refine((value) => value.resolution !== 'verified' || (value.spotifyId ?? '').length > 0, {
    message: 'A verified identity needs a Spotify id.',
    path: ['spotifyId'],
  })
  .meta({ id: 'ResolveArtistIdentityRequest' });
export type ResolveArtistIdentityRequest = z.infer<typeof resolveArtistIdentityRequestSchema>;

export const BOOKING_REGISTER_SORT_KEYS = [
  'name',
  'cost',
  'genre',
  'artistType',
  'agency',
  'status',
  /* QA13 24: stjerna (ønskelista). `ix_booking_artists__shortlisted` ordner nøyaktig dette keysettet. */
  'shortlisted',
  /* B4 (sak 273): Soundcharts-tallene, fra `sc_artist_stats`. */
  'monthlyListeners',
  'listenersGrowth30d',
  'topAgeGroup',
  'instagramFollowers',
] as const;
export type BookingRegisterSortKey = (typeof BOOKING_REGISTER_SORT_KEYS)[number];

// ------------------------------------------------------------------ flervalgsfiltrene (#24d)

/**
 * Formen et flervalgsfilter har i en URL: gjentatt (`?genre=pop&genre=rock`) eller kommaliste
 * (`?genre=pop,rock`).
 *
 * `tasks.ts` har de samme to formene for de samme grunnene — kommalista er den kortere adressen og
 * den som er til å redigere for hånd. Skrevet ut per filter framfor bak en generisk hjelper, fordi
 * en `z.ZodType`-returnerende hjelper mister elementtypen og gjør hele spørringen `unknown`.
 */
const splitList = (value: unknown): unknown => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',');
};

/** Sentinelen «uten byrå» i byrålista. Ikke en uuid, så den kan ikke kollidere med en ekte id. */
export const NO_AGENCY_FILTER = 'none';

/** Id-filteret i registeret: et utvalg servert av klienten, ikke en avkryssingsliste. */
const MAX_ID_FILTER_VALUES = 200;

/** Tjue er lånt fra `uuidListQuerySchema` i contacts: en avkryssingsliste ingen huker av mer i. */
const MAX_FILTER_VALUES = 20;

const statusListQuerySchema = z.preprocess(
  splitList,
  z.array(bookingArtistStatusSchema).min(1).max(BOOKING_ARTIST_STATUSES.length),
);
const genderListQuerySchema = z.preprocess(
  splitList,
  z.array(bookingGenderSchema).min(1).max(BOOKING_GENDERS.length),
);
const agencyListQuerySchema = z.preprocess(
  splitList,
  z
    .array(z.union([z.uuid(), z.literal(NO_AGENCY_FILTER)]))
    .min(1)
    .max(MAX_FILTER_VALUES),
);
/** Sjanger og artisttype er FRITEKST i registeret — prinsipp 9, ingen hardkodet liste. */
const textListQuerySchema = z.preprocess(
  splitList,
  z.array(z.string().trim().min(1).max(MAX_BOOKING_LABEL)).min(1).max(MAX_FILTER_VALUES),
);

export const listBookingRegisterQuerySchema = cursorQuerySchema(BOOKING_REGISTER_SORT_KEYS)
  .extend({
    search: z.string().trim().min(1).max(120).optional(),
    /**
     * «Vis meg dem som trenger en avklaring» — `ambiguous` og `not_found` i én sving.
     *
     * Et eget flagg framfor et statusfilter med to verdier: køen er ÉN ting for den som skal
     * jobbe seg gjennom den, og en liste som krever at man vet hvilke to av fire statuser som
     * betyr «uavklart» er en liste ingen åpner.
     */
    needsIdentity: z
      .preprocess((value) => (value === 'true' || value === true ? true : undefined), z.boolean())
      .optional(),
    /** Bare de skjulte artistene (237.1, filteret «Skjulte»). Uten den står de ikke i lista. */
    hidden: z
      .preprocess((value) => (value === 'true' || value === true ? true : undefined), z.boolean())
      .optional(),
    /** Ta med arkiverte artister (215.29). Uten den skjuler lista dem. */
    includeArchived: z
      .preprocess((value) => (value === 'true' || value === true ? true : undefined), z.boolean())
      .optional(),
    /**
     * FEM FLERVALGSFILTRE, OG DE ER FLERVALG PÅ SERVEREN (QA-runde 12, #24d).
     *
     * ---------------------------------------------------------------------------------------
     * Atle: «Flervalg i alle filtre (Byrå, Sjanger, Artisttype, Kjønn, Status). OR innen samme
     * filter, AND mellom filtre. Valgene ligger i URL-en.»
     *
     * SJANGER OG ARTISTTYPE FLYTTET HIT FRA KLIENTEN, og det er en RETTELSE og ikke bare et nytt
     * filter. De to ble filtrert med en `filter()` på radene `useInfiniteQuery` tilfeldigvis hadde
     * hentet — nøyaktig løgnen kommentaren over `minCostMinor` beskriver: «en klient som filtrerte
     * selv ville filtrert innenfor den SIDEN den hadde hentet». Målt 19. september: «Sjanger: pop»
     * på side 1 av 30 ga 4 treff av 130, og sidetelleren sa fortsatt 741.
     *
     * Formen er den `tasks.ts` alt bruker: gjentatt (`?status=a&status=b`) eller kommaliste
     * (`?status=a,b`). Kommalista er den kortere URL-en og den som er til å redigere for hånd.
     */
    status: statusListQuerySchema.optional(),
    agencyId: agencyListQuerySchema.optional(),
    /**
     * «Uten byrå» som en verdi i BYRÅFILTERET, ikke som et eget flagg.
     *
     * «Hvilket byrå» har alltid hatt «ingen» som et lovlig svar, og i et flervalg må «Standing
     * Ovation ELLER ingen» kunne uttrykkes. `agencyId=none,<uuid>` er den formen; `none` er ikke
     * en uuid og kan derfor ikke forveksles med en ekte id.
     */
    shortlisted: z
      .preprocess(
        (value) => (value === 'true' ? true : value === 'false' ? false : value),
        z.boolean(),
      )
      .optional(),
    gender: genderListQuerySchema.optional(),
    /**
     * «BARE DE JEG SAMMENLIGNER» (QA13 24). Sammenligningen bor i nettleseren, per person
     * (`comparison.tsx`), så filteret sender id-ene. «Venter på meg/oss» (C9) sender artistene med en
     * kontrakt som venter på en signatur, og de kan være flere enn tjue — derfor et eget tak.
     */
    ids: z.preprocess(splitList, z.array(z.uuid()).min(1).max(MAX_ID_FILTER_VALUES)).optional(),
    /** Sjangrene og typene registeret faktisk bruker — fritekst, så ingen enum å bryne seg på. */
    genre: textListQuerySchema.optional(),
    artistType: textListQuerySchema.optional(),
    /**
     * «UTEN PRIS» (#24f).
     *
     * Rapporten: «Artister uten pris viser "~ 0 kr". Vis "—". De skal ikke falle ut av "alle", og
     * filteret får valget "Uten pris".» Et eget flagg og ikke `maxCostMinor=1`, fordi de to er
     * ulike spørsmål: null kroner er «vi har ikke ført inn noe», ikke «hun er billig».
     */
    noPrice: z
      .preprocess((value) => (value === 'true' || value === true ? true : undefined), z.boolean())
      .optional(),
    priceConfirmed: z
      .preprocess(
        (value) => (value === 'true' ? true : value === 'false' ? false : value),
        z.boolean(),
      )
      .optional(),
    /**
     * PRISNIVÅ SOM ET INTERVALL PÅ SERVEREN, ikke som en avgrensning i klienten.
     *
     * Greenroom har fire faste trinn — under 100k, 100–300k, 300–600k, over 600k. De hører hjemme
     * på serveren fordi lista er PAGET: en klient som filtrerte selv ville filtrert innenfor den
     * SIDEN den hadde hentet, og «under 100k» ville gitt tre treff fordi de andre lå på side to.
     *
     * `maxCostMinor` er EKSKLUSIV, så to trinn ved siden av hverandre ikke begge fanger 300 000.
     */
    minCostMinor: z.coerce.number().int().min(0).optional(),
    maxCostMinor: z.coerce.number().int().min(0).optional(),
  })
  .meta({ id: 'ListBookingRegisterQuery' });
export type ListBookingRegisterQuery = z.infer<typeof listBookingRegisterQuerySchema>;

/** Å arkivere en artist fra byråets stall, eller hente henne fram igjen (215.29). */
export const archiveBookingArtistRequestSchema = z
  .object({ archived: z.boolean() })
  .meta({ id: 'ArchiveBookingArtistRequest' });
export type ArchiveBookingArtistRequest = z.infer<typeof archiveBookingArtistRequestSchema>;

/** Å skjule en artist fra skanningen eller importen, eller hente henne tilbake (237.1). */
export const hideBookingArtistRequestSchema = z
  .object({ hidden: z.boolean() })
  .meta({ id: 'HideBookingArtistRequest' });
export type HideBookingArtistRequest = z.infer<typeof hideBookingArtistRequestSchema>;

export const bookingRegisterListResponseSchema = page(
  bookingRegisterArtistDtoSchema,
  'BookingRegisterListResponse',
)
  .extend({
    /**
     * Hvor mange artister registeret har i alt.
     *
     * ET BEVISST UNNTAK FRA PRINSIPP 6, som forbyr COUNT(*). Atle, 7. september: «liker bedre
     * hvordan man velger sider på greenroom» — og greenrooms sidevelger viser «Viser 1–25 av 689
     * artister». Sidetall uten et totalantall er ikke sidetall.
     *
     * Målt før det ble bygget: 1,4 ms på 735 rader. Det er en seq scan, så den vokser lineært, og
     * derfor står unntaket som sak 91 i TIL-GODKJENNING framfor å bli en ny vane. Feltet finnes
     * BARE her — page() er urørt, og de andre listene teller fortsatt ingenting.
     */
    total: z.number().int().nonnegative(),
  })
  .meta({ id: 'BookingRegisterListResponse' });
export type BookingRegisterListResponse = z.infer<typeof bookingRegisterListResponseSchema>;

const amountField = z.number().int().min(0).max(MAX_COST_AMOUNT_MINOR);

/**
 * BUDETS HONORAR ER OVER 0 (AI-ANALYSE §8.1 #4). Sto bare i budmodalen. `amountField` tillater 0 fordi
 * den også er kostnader og priser; et bud på null kroner er derimot ikke et bud.
 */
function positiveAmountIssue(ctx: z.RefinementCtx): void {
  ctx.addIssue({
    code: 'custom',
    path: ['amountMinor'],
    message: 'A bid must be above 0.',
    params: { code: 'amount_must_be_positive' },
  });
}

export const createBookingArtistRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_BOOKING_NAME),
    agencyId: z.uuid().nullish(),
    /*
     * `contactId`, `score` OG `monthlyListeners` STO HER TIL 29. SEPTEMBER (tabeller, §4 #1 og #4).
     *
     * `contactId` ble tatt imot og aldri skrevet: agenten er byråets kontakt på artisten
     * (`booking_artist_contacts`, satt av byrået i sin stall eller av skanningen), ikke et felt
     * festivalen eier. Scoren og lyttertallet er innhentet/beregnet (dummy til analysen kommer), og
     * en klient som skrev dem ville overstyrt det analysen skal si. Ukjente felt strippes av Zod.
     */
    genre: z.string().trim().min(1).max(MAX_BOOKING_LABEL).nullish(),
    artistType: z.string().trim().min(1).max(MAX_BOOKING_LABEL).nullish(),
    homeTown: z.string().trim().min(1).max(MAX_BOOKING_LABEL).nullish(),

    /** `null` tømmer honoraret (272.2). */
    feeMinor: amountField.nullish(),
    travelMinor: amountField.optional(),
    accommodationMinor: amountField.optional(),
    foodMinor: amountField.optional(),
    currency: z.string().length(3).optional(),
    /** `null` tømmer antallet. */
    partySize: z.number().int().min(1).max(MAX_PARTY_SIZE).nullish(),
    /**
     * «Bruk satsene»: `rates` regner reise, opphold og mat av satsene × antallet igjen. Et beløp
     * som sendes, gjør raden `set` — det sendte vinner over flagget.
     */
    extrasSource: z.enum(['rates', 'set']).optional(),

    commissionBp: z.number().int().min(0).max(10000).nullish(),

    status: bookingArtistStatusSchema.optional(),
    shortlisted: z.boolean().optional(),
    /** `null` fjerner den ansvarlige. Se DTO-en for hvorfor den bor på artisten (sak 118). */
    assigneePersonId: z.uuid().nullish(),
    imageUrl: urlInputSchema(500).nullish(),
    notes: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),

    priceConfirmed: z.boolean().optional(),
    gender: bookingGenderSchema.nullish(),
    frontPerson: z.string().trim().min(1).max(MAX_BOOKING_NAME).nullish(),
    ageGroup: z.string().trim().min(1).max(MAX_BOOKING_LABEL).nullish(),
    spotifyUrl: urlInputSchema(500).nullish(),
    /** Uten @ og uten URL — «auroramusic», ikke «@auroramusic» eller hele lenka. */
    instagramHandle: z.string().trim().min(1).max(60).nullish(),
    instagramFollowers: z.number().int().min(0).nullish(),
    /** Lenkefeltene for TikTok og YouTube (1003, sak 273). En lenke satt her vinner over Soundcharts. */
    tiktokUrl: urlInputSchema(500).nullish(),
    youtubeUrl: urlInputSchema(500).nullish(),
  })
  .meta({ id: 'CreateBookingArtistRequest' });
export type CreateBookingArtistRequest = z.infer<typeof createBookingArtistRequestSchema>;

export const updateBookingArtistRequestSchema = createBookingArtistRequestSchema
  .partial()
  .meta({ id: 'UpdateBookingArtistRequest' });
export type UpdateBookingArtistRequest = z.infer<typeof updateBookingArtistRequestSchema>;

// ------------------------------------------------------------------ utkast og brett

export const bookingDraftDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    name: z.string(),
    notes: z.string().nullable(),
    /** Hvor mange artister som står i det, så velgeren kan si det uten å hente brettet. */
    itemCount: z.number().int().nonnegative(),
    /** Summen av de plasserte artistenes fire beløp. Utkastets pris. Null = skjult (K2 funn 3). */
    totalMinor: z.number().int().nonnegative().nullable(),
    currency: z.string(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'BookingDraftDto' });
export type BookingDraftDto = z.infer<typeof bookingDraftDtoSchema>;

/** Ikke paget: en årgang har en håndfull utkast, og velgeren viser dem alle. */
export const bookingDraftListResponseSchema = z
  .object({ items: z.array(bookingDraftDtoSchema) })
  .meta({ id: 'BookingDraftListResponse' });
export type BookingDraftListResponse = z.infer<typeof bookingDraftListResponseSchema>;

export const createBookingDraftRequestSchema = z
  .object({
    editionId: z.uuid(),
    name: z.string().trim().min(1).max(MAX_DRAFT_NAME),
    notes: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
    /** Kopier plasseringene fra et annet utkast. «Utkast B, men uten Kvitbjørn». */
    copyFromDraftId: z.uuid().nullish(),
  })
  .meta({ id: 'CreateBookingDraftRequest' });
export type CreateBookingDraftRequest = z.infer<typeof createBookingDraftRequestSchema>;

export const updateBookingDraftRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_DRAFT_NAME).optional(),
    notes: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
  })
  .meta({ id: 'UpdateBookingDraftRequest' });
export type UpdateBookingDraftRequest = z.infer<typeof updateBookingDraftRequestSchema>;

/**
 * Én artist på én scene på én dag.
 *
 * `day` er en DATO og `startsAt`/`endsAt` er KLOKKESLETT, begge uten den andre. Et utkast er
 * «Kvitbjørn på Hovedscenen fredag 21:00», og å lagre det som et øyeblikk ville krevd en tidssone
 * for noe som ikke er avtalt. Klokka settes på ordentlig når raden blir en programpost.
 */
export const bookingDraftItemDtoSchema = z
  .object({
    id: z.uuid(),
    bookingArtistId: z.uuid(),
    artistName: z.string(),
    agencyName: z.string().nullable(),
    stageId: z.uuid(),
    day: z.iso.date(),
    /** `HH:MM`. Null så lenge man bare har bestemt scene og dag. */
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    sortOrder: z.number().int(),
    /** Artistens fire beløp lagt sammen, så budsjettlinja er ett kall og ikke femti. Null = skjult. */
    totalMinor: z.number().int().nonnegative().nullable(),
    status: bookingArtistStatusSchema,
    /**
     * HVA «FLYTT OVER I PROGRAMMET» KOMMER TIL Å GJØRE MED DENNE RADEN (QA-punkt 18).
     *
     * Dialogen talte i klienten på det brettet bar, og brettet visste verken om raden alt var
     * flyttet eller om budet var godtatt. Da sa den «Flyttes over: 5» og flyttet én.
     *
     * Begge svarene kommer nå fra serveren, regnet med det SAMME uttrykket promote() bruker — se
     * READY_TO_PROMOTE. To steder som teller hver for seg blir uenige.
     */
    promoted: z.boolean(),
    readyToPromote: z.boolean(),
    /** Et godtatt bud på plasseringen — avgjør statusen «Flytt over» gir (`promotionStatusFor`, QA13 60). */
    hasAcceptedBid: z.boolean(),
    /** Satt til bekreftet for hånd, uten bud eller forespørsel i Tvitvi for årgangen (QA13 60). */
    confirmedByHand: z.boolean(),
    /** Saken for artisten i årgangen (0927): det «Flytt over» leser statusen av (QA13 60). */
    engagement: engagementRefDtoSchema.nullable().optional(),
    /**
     * SETTLENGDEN, SÅ BRETTET KAN REGNE UT SLUTTIDEN (QA-runde 12, #29a).
     *
     * «Slutt settes til start + settlengde fra format eller bud.» Tallet fantes allerede på
     * serveren — `accepted_set_minutes` i brettspørringen, brukt til å regne sluttid ved
     * overføring (QA M1) — men det kom aldri ut til skjermen, så tidsfeltet hadde ingenting å
     * legge til med.
     *
     * `null` er «ingen har sagt hvor lenge hun spiller», og da lar kontrollen slutten stå tom. En
     * standard på seksti minutter ville vært et tall vi fant på, skrevet inn i en kjøreplan.
     */
    setLengthMinutes: z.number().int().positive().nullable(),
    /**
     * HVA SOM ALT STÅR PÅ SCENEN PÅ DET TIDSPUNKTET (sak 217.25): artistnavnet på en annen
     * plassering i utkastet, eller tittelen på en programpost i årgangen. Regnet av `board()`,
     * med Lineups regel — avlyst teller ikke, uten klokkeslett er det ingenting å kollidere om, og
     * slutt = start er ingen kollisjon. Tom eller fraværende når ingenting kolliderer.
     */
    stageConflicts: z.array(z.string()).optional(),
  })
  .meta({ id: 'BookingDraftItemDto' });
export type BookingDraftItemDto = z.infer<typeof bookingDraftItemDtoSchema>;

/** Scenen slik brettet trenger den. Kommer fra ÅRGANGEN — det er integrasjonen Atle ba om. */
export const bookingBoardStageSchema = z
  .object({ id: z.uuid(), name: z.string(), sortOrder: z.number().int() })
  .meta({ id: 'BookingBoardStage' });
export type BookingBoardStage = z.infer<typeof bookingBoardStageSchema>;

/**
 * Hele brettet i ett svar: dagene, scenene og plasseringene.
 *
 * Ett kall og ikke tre, fordi de tre er meningsløse hver for seg — en plassering peker på en scene
 * og en dag, og en klient som hentet dem i tre runder ville tegnet et halvt brett i mellomtiden.
 * Størrelsen er avgrenset av virkeligheten: en årgang har noen få dager, noen få scener, og et
 * program som får plass på en plakat.
 */
export const bookingBoardResponseSchema = z
  .object({
    draft: bookingDraftDtoSchema,
    /** Årgangens dager, fra `starts_on` til `ends_on`. Tom hvis årgangen ikke har datoer ennå. */
    days: z.array(z.iso.date()),
    stages: z.array(bookingBoardStageSchema),
    items: z.array(bookingDraftItemDtoSchema),
    /** Årgangens bookingbudsjett, så linja over brettet kan tegnes uten et kall til Økonomi. Null = skjult. */
    budgetMinor: z.number().int().nullable(),
    currency: z.string(),
  })
  .meta({ id: 'BookingBoardResponse' });
export type BookingBoardResponse = z.infer<typeof bookingBoardResponseSchema>;

/** `HH:MM`. Ikke sekunder: et utkast er ikke en kjøreplan. */
export const CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const clockSchema = z.string().regex(CLOCK_PATTERN);

export const createBookingDraftItemRequestSchema = z
  .object({
    bookingArtistId: z.uuid(),
    stageId: z.uuid(),
    day: z.iso.date(),
    startsAt: clockSchema.nullish(),
    endsAt: clockSchema.nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateBookingDraftItemRequest' });
export type CreateBookingDraftItemRequest = z.infer<typeof createBookingDraftItemRequestSchema>;

/**
 * Å flytte en plassering. Alt er valgfritt fordi de tre skjer hver for seg: man drar den til en
 * annen scene, eller til en annen dag, eller setter et klokkeslett på den.
 */
export const updateBookingDraftItemRequestSchema = z
  .object({
    stageId: z.uuid().optional(),
    day: z.iso.date().optional(),
    startsAt: clockSchema.nullish(),
    endsAt: clockSchema.nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'UpdateBookingDraftItemRequest' });
export type UpdateBookingDraftItemRequest = z.infer<typeof updateBookingDraftItemRequestSchema>;

// ------------------------------------------------------------------ bud og interesse

/**
 * INGENTING HER SENDER NOE. Atle: «Lag frontend nå, men så venter vi med backend for denne da vi må
 * ha laget portalen for bookingbyråene også for å integrere det.»
 *
 * `sentAt` settes av den som trykker «meldt», og betyr «dette er sendt av et menneske, utenfor
 * systemet». Når portalen kommer, er det `status` og et svar som kobles på — ikke en ny tabell og
 * ikke en ny kontrakt.
 */
export const bookingBidDtoSchema = z
  .object({
    id: z.uuid(),
    bookingArtistId: z.uuid(),
    artistName: z.string(),
    agencyName: z.string().nullable(),
    editionId: z.uuid().nullable(),
    kind: bookingBidKindSchema,
    /** Alltid null for `interest`. Skranken i basen håndhever det. */
    amountMinor: z.number().int().nullable(),
    currency: z.string(),
    /**
     * AVTALETYPEN SOM EGET FELT (sak 222.43, 0430). Null er et bud som bare sier beløpet (alle
     * festivalbud og alle bud før 0430). Prosenten gjelder versus og dørsplitt.
     */
    dealType: z.enum(CONCERT_DEAL_TYPES).nullable(),
    dealArtistPercent: z.number().nullable(),
    dealAfterCosts: z.boolean().nullable(),
    sentAt: z.iso.datetime().nullable(),
    respondsBy: z.iso.date().nullable(),
    status: bookingBidStatusSchema,

    /**
     * PLASSERINGEN BUDET GJELDER, og de tjueto vilkårene.
     *
     * `draftItemId` er null for en INTERESSE og satt for et bud — regelen Atle ba om: «Ønsker man
     * å legge inn et konkret bud må artisten være tilknyttet et arrangement eller venue.»
     *
     * `terms` har den samme formen kontrakten får ved overføring. Se `booking-terms.ts`.
     */
    draftItemId: z.uuid().nullable(),
    /**
     * SALEN, når budet er plassert i et spillested (sak 230, 0455). Da er `stageName` salen og
     * `editionName` spillestedet — aldri årgangen, som et hus ikke viser.
     */
    venueRoomId: z.uuid().nullable(),
    /** Programposten budet ble til (konserten, 0424, eller arrangementet, 0455). */
    performanceId: z.uuid().nullable(),
    /** Arrangementet budet ble til, og huset det står i — for lenken «Åpne arrangement». */
    venueNightId: z.uuid().nullable(),
    /**
     * ARRANGEMENTETS TRINN (I2, funn 33), i stigens ord. Budet har budets ord (`status`); der Booking
     * viser arrangementet, er det dette.
     */
    venueNightStatus: z.enum(VENUE_NIGHT_STATUSES).nullable(),
    venueFestivalId: z.uuid().nullable(),
    /** Slått opp for visning, så budet kan si «Hovedscenen, God Nok 2027» uten et kall til. */
    stageName: z.string().nullable(),
    editionName: z.string().nullable(),
    terms: bidTermsSchema,
    payments: z.array(bidPaymentSchema),

    /**
     * BYRÅETS SVAR (migrering 0086), skrevet av dem gjennom krysningspunktet.
     *
     * `unknown` er tilstanden FØR noen har svart, og den er ikke det samme som `busy`: en liste som
     * blandet de to ville sagt at artisten er opptatt overalt vi ennå ikke har spurt.
     *
     * `askingFeeMinor` er BYRÅETS tall, ikke vårt bud. De to står ved siden av hverandre fordi det
     * er der forhandlingen faktisk skjer.
     */
    availability: enquiryAvailabilitySchema,
    holdUntil: z.iso.date().nullable(),
    askingFeeMinor: z.number().int().nullable(),
    answeredAt: z.iso.datetime().nullable(),

    responseNote: z.string().nullable(),
    /** Teksten som ble sendt til byrået — se createBookingBidRequestSchema. */
    message: z.string().nullable(),
    /** Nyeste innlegg i budets egen tråd. Null når ingen har skrevet noe (QA H2). */
    lastMessage: z.string().nullable(),
    /**
     * Om byrået bak artisten er en organisasjon i Tvitvi med en akseptert kobling (QA H2).
     *
     * Skjermen bruker den til ÉN ting: å slutte å tilby festivalen å sette de statusene byrået
     * eier. Er byrået ikke her, er nedtrekket fortsatt hele integrasjonen.
     */
    agencyInTvitvi: z.boolean(),
    /**
     * DA BUDET FAKTISK LANDET I MOTPARTENS INNBOKS (QA12, 32a).
     *
     * Null betyr «aldri levert», og det er en opplysning arrangøren skal se framfor å oppdage den
     * når svaret aldri kommer. Den regnes IKKE ut av om byrået er i Tvitvi i dag: et byrå som
     * kobler seg til i morgen gjør ikke gårsdagens forespørsel levert.
     */
    deliveredAt: z.iso.datetime().nullable(),
    /** Huset den ble levert til, med navn — slik raden kan si «Levert til Standing Ovation». */
    deliveredToName: z.string().nullable(),
    /**
     * HVEM I HUSET SOM SENDTE DEN (QA-runde 12, K-5).
     *
     * ---------------------------------------------------------------------------------------
     * K-5 fjerner «Ansvarlig» fra festivalens artistkort, og setter noe annet i stedet: «I stedet
     * markeres det hvem som har SENDT forespørsel, interesse, bud og meldinger: "Sendt av Atle" i
     * Dialog, i artistens Bud- og Samtale-fane og i advancen.»
     *
     * Det er en bedre opplysning enn den den erstatter, og grunnen er at den er SANN uten at noen
     * har fylt den ut. «Ansvarlig» var et felt et menneske måtte sette og som sto tomt på 741
     * rader; «sendt av» er en kjensgjerning raden allerede bar — `created_by` har stått i hver
     * eneste budrad siden fase 0, uten at noen leste den.
     *
     * Null for en rad ingen person opprettet — speilede rader fra motparten, og rader seeden
     * laget. Skjermen tegner da ingenting framfor «Sendt av —».
     */
    sentByName: z.string().nullable(),
    /**
     * HVEM SOM SKREV SISTE MELDING (QA15 funn 38): en person i vårt hus, ellers huset som skrev
     * (byrået). Uten melding: den som sendte raden. Valgfri for eldre klienter.
     */
    lastMessageBy: z.string().nullable().optional(),
    /**
     * HVORFOR BUDET ER TRUKKET (1211, QA15 funn 16): `by_us` når et menneske hos oss trakk det,
     * `superseded` når et nytt bud på samme plass erstattet det. Null ellers, og for eldre rader.
     */
    withdrawnReason: z.enum(['by_us', 'superseded']).nullable().optional(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** Engasjementet raden hører til (0927, grep F1): den ene statusen alle flatene leser. */
    engagement: engagementRefDtoSchema.nullable().optional(),
  })
  .meta({ id: 'BookingBidDto' });
export type BookingBidDto = z.infer<typeof bookingBidDtoSchema>;

/**
 * BYRÅETS EGEN PROFIL OM DEN SAMME ARTISTEN (sak 116.2, QA H11, migrering 0151).
 *
 * ---------------------------------------------------------------------------------------------
 * Atle valgte 116.2: festivalens registerrad beholder sitt, og byråets tall står VED SIDEN AV.
 * Vedtak 96.1 — de to radene er hver sin sak — står uendret.
 *
 * ET EGET KALL OG IKKE ET FELT PÅ ARTISTRADEN. Lista er 741 rader lang, og profilen krever et
 * oppslag over husgrensen per rad. Den som vil se forskjellen åpner artisten; lista skal bli
 * tegnet.
 *
 * ALT KAN VÆRE NULL. Byrået kan ha raden uten å ha fylt den ut, og en tom linje er et ærligere
 * svar enn et gjettet.
 */
export const agencyArtistProfileSchema = z
  .object({
    /** Huset tallene kommer fra. Linja sier navnet, for det er halve opplysningen. */
    agencyName: z.string(),
    genre: z.string().nullable(),
    homeTown: z.string().nullable(),
    /**
     * STANDARDFORMATETS REISEFØLGE, MED NAVNET SITT (§5.2, QA-punkt 20).
     *
     * Atle, 11. september: «tallet på byrålinja tilhører også et format. Står det bare "7
     * reiser", er det samme problemet som QA-punktet pekte på, bare flyttet.»
     *
     * De to hører sammen og kommer fra den samme raden. Har artisten flere størrelser, teller
     * skjermen dem i stedet for å vise ett av tallene som om det var artistens.
     */
    travellingParty: z.number().int().nullable(),
    defaultFormatName: z.string().nullable(),
    /**
     * STANDARDFORMATETS SPILLELENGDE (§5.2, QA-punkt 7, migrering 0162/0163).
     *
     * Rapporten: «budet foreslår 60 min selv om formatet sier 75». Tallet står i byråets hus, på
     * størrelsen, og kommer fra den samme raden som `defaultFormatName` — av den samme grunnen som
     * reisefølget over: hentet hver for seg kunne de en dag beskrevet hvert sitt format.
     *
     * Null er «ingen har sagt det», ikke null minutter, og da står husets standard.
     */
    defaultSetLengthMinutes: z.number().int().nullable(),
    formats: z.array(z.string()),
    /**
     * FORMATENE BYRÅET HAR DELT, HELE (B5, 1001): samme port som `formats`. Artistpanelets
     * Oversikt viser kortet «Formater» bare når denne har noe. Med standard, så et API som ikke er
     * startet på nytt ikke gir kontraktbrudd.
     */
    formatDetails: z
      .array(
        z.object({
          name: z.string(),
          isDefault: z.boolean(),
          travellingParty: z.number().int().nullable(),
          setLengthMinutes: z.number().int().nullable(),
          description: z.string().nullable(),
        }),
      )
      .default([]),
    /** Byråets egen tekst om artisten, når byrået har delt noe. Null: ingen tekst. */
    pitch: z.string().nullable().default(null),
  })
  .meta({ id: 'AgencyArtistProfile' });
export type AgencyArtistProfile = z.infer<typeof agencyArtistProfileSchema>;

/** Fraværende når byrået ikke er i Tvitvi, eller ikke har artisten. */
export const agencyArtistProfileResponseSchema = z
  .object({ profile: agencyArtistProfileSchema.nullable() })
  .meta({ id: 'AgencyArtistProfileResponse' });
export type AgencyArtistProfileResponse = z.infer<typeof agencyArtistProfileResponseSchema>;
/** Ikke paget: budene på én artist er en håndfull, og artistkortet leser dem hele. */
export const bookingBidListResponseSchema = z
  .object({ items: z.array(bookingBidDtoSchema) })
  .meta({ id: 'BookingBidListResponse' });
export type BookingBidListResponse = z.infer<typeof bookingBidListResponseSchema>;

/**
 * «HVEM OPPTRER?»-SØKET (QA16 funn 6). Treffene er sortert av serveren over hele registeret: eksakt
 * navn, så navn som begynner med søket, så resten; innenfor hver: godtatt bud, så Booking, så den
 * offisielle lista. Et godtatt bud som venter på et arrangement, er sitt eget treff (`source: 'bid'`) foran
 * artisten selv, så budet kan velges og artisten likevel bookes en annen dag.
 */
export const performerSearchQuerySchema = z
  .object({
    q: z.string().trim().min(1).max(120),
    /** Godtatte bud teller bare der leseren kan lage arrangement av dem («Nytt arrangement»). */
    withBids: z.stringbool().optional(),
  })
  .meta({ id: 'PerformerSearchQuery' });
export type PerformerSearchQuery = z.infer<typeof performerSearchQuerySchema>;
export const performerSearchHitSchema = z
  .object({
    artist: bookingRegisterArtistDtoSchema,
    bid: bookingBidDtoSchema.nullable(),
    source: z.enum(['bid', 'booking', 'register']),
  })
  .meta({ id: 'PerformerSearchHit' });
export type PerformerSearchHit = z.infer<typeof performerSearchHitSchema>;
export const performerSearchResponseSchema = z
  .object({ items: z.array(performerSearchHitSchema) })
  .meta({ id: 'PerformerSearchResponse' });
export type PerformerSearchResponse = z.infer<typeof performerSearchResponseSchema>;

/**
 * DIALOGEN — all kontakt med byråene, på tvers av artister.
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 7. september: «Legg til en tredje som omhandler all kontakt man har i budgivning og
 * forespørsler av artister.»
 *
 * Artistkortet svarer på «hva har vi sagt til DENNE», og det er et annet spørsmål enn det man
 * åpner denne fanen for: «hvem venter vi svar fra, og hva har vi glemt å følge opp». Det andre
 * spørsmålet kan ikke stilles én artist om gangen — det er nettopp summen som er svaret.
 *
 * ---------------------------------------------------------------------------------------------
 * `open` ER ETT FILTER OG IKKE TO STATUSER, og det er hele poenget med fanen.
 *
 * «Meldt, men ubesvart» er tilstanden en bookingansvarlig jager: budet er sendt, byrået har ikke
 * svart, og fristen nærmer seg. Å be klienten om å be om `status=sent` OG huske at `countered`
 * også venter på oss, ville vært å legge den definisjonen i hver klient som spør.
 */
export const BID_DIALOGUE_FILTERS = ['all', 'open', 'answered'] as const;
export const bidDialogueFilterSchema = z
  .enum(BID_DIALOGUE_FILTERS)
  .meta({ id: 'BidDialogueFilter' });
export type BidDialogueFilter = (typeof BID_DIALOGUE_FILTERS)[number];

/**
 * Sorteringene Dialog-fanen tilbyr (QA A9).
 *
 * Atle: «ALLE TABELLER MÅ KUNNE SØKES OG SORTERES!!!» Fanen hadde to nøkler og ingen sorterbare
 * kolonner, så lista kom i den rekkefølgen serveren tilfeldigvis ga.
 *
 * De fire nye er de fire spørsmålene man faktisk stiller en slik liste: hvilken artist, hvilket
 * byrå, hva slags henvendelse, og hvor den står.
 */
export const BID_DIALOGUE_SORT_KEYS = [
  'created',
  'respondsBy',
  'artist',
  'agency',
  'kind',
  'status',
] as const;
export type BidDialogueSortKey = (typeof BID_DIALOGUE_SORT_KEYS)[number];

export const listBidDialogueQuerySchema = cursorQuerySchema(BID_DIALOGUE_SORT_KEYS)
  .extend({
    show: bidDialogueFilterSchema.optional(),
    kind: bookingBidKindSchema.optional(),
    status: bookingBidStatusSchema.optional(),
    /** Fritekst mot artistnavn og byrånavn — det man faktisk husker når man leter. */
    search: z.string().trim().min(1).max(120).optional(),
  })
  .meta({ id: 'ListBidDialogueQuery' });
export type ListBidDialogueQuery = z.infer<typeof listBidDialogueQuerySchema>;

/**
 * PAGET, i motsetning til lista på ett artistkort.
 *
 * En organisasjon som har booket i tre år har tusenvis av rader her, og prinsipp 6 forbyr `OFFSET`.
 * `summary` er tellingene fanens egne chips viser, og de er regnet av SERVEREN: en telling gjort på
 * den siden man tilfeldigvis har hentet ville sagt «3 venter svar» når det var nittifire.
 */
export const bidDialogueResponseSchema = page(bookingBidDtoSchema, 'BidDialogueResponse')
  .extend({
    summary: z.object({
      open: z.number().int().nonnegative(),
      answered: z.number().int().nonnegative(),
      /** Frist passert og fortsatt ubesvart — den ene tellingen som betyr «ring nå». */
      overdue: z.number().int().nonnegative(),
    }),
  })
  /*
   * `.meta()` PÅ NYTT ETTER `.extend()`, fordi utvidelsen lager et NYTT skjema uten den.
   * `dto.spec.ts` fanget det med én setning som gjaldt hele suiten: «Every DTO schema must call
   * .meta({ id })». Uten den ville komponenten fått et generert navn som endrer seg av seg selv.
   */
  .meta({ id: 'BidDialogueResponse' });
export type BidDialogueResponse = z.infer<typeof bidDialogueResponseSchema>;

/**
 * `kind` bestemmer om `amountMinor` er lovlig, og `superRefine` sier det i kontrakten framfor å
 * la basen si det i en 500. En interesse med et beløp er ikke en liten feil — det er en klient som
 * har vist feil skjema.
 */
export const createBookingBidRequestSchema = z
  .object({
    bookingArtistId: z.uuid(),
    editionId: z.uuid().nullish(),
    kind: bookingBidKindSchema,
    amountMinor: amountField.nullish(),
    currency: z.string().length(3).optional(),
    respondsBy: z.iso.date().nullish(),
    /**
     * DATOEN OG STEDET FORESPØRSELEN GJELDER.
     *
     * Atle, 11. september: «når jeg sender en forespørsel kommer ikke datoen jeg spesifiserer med.
     * Det står bare hos bookingbyrået "ingen dato valgt".»
     *
     * Skjemaet spurte om begge, fylte dem til og med ut selv når artisten sto i programmeringen —
     * og brukte dem så BARE til å skrive setningen i meldingsfeltet. De ble aldri sendt.
     *
     * Et BUD henter dem fortsatt fra plasseringen, som er den eneste sannheten der: en dato på
     * budet som ikke er dagen artisten står på brettet ville vært to svar på det samme spørsmålet.
     * En FORESPØRSEL har ingen plassering — det er hele forskjellen på de to — så her er feltene
     * det brukeren skrev.
     */
    performanceDate: z.iso.date().nullish(),
    venueName: z.string().trim().min(1).max(200).nullish(),
    notes: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
    /**
     * TEKSTEN SOM GÅR TIL BYRÅET — noe annet enn notatet vårt.
     *
     * Atle, 8. september: «mulighet for å skrive en melding. Gjerne lag en auto generert versjon
     * der som kan redigeres.» Feltet notes er VÅRT notat om budet — «ringte Lasse, han sjekker» — og
     * to ting som deler én kolonne blir til at den ene overskriver den andre.
     */
    message: z.string().trim().min(1).max(MAX_BID_MESSAGE).nullish(),
    /** Marker som meldt med én gang. `false` lagrer den som et utkast man kan rette først. */
    markSent: z.boolean().optional(),
    /**
     * PLASSERINGEN. Påkrevd for et bud, forbudt for en interesse — `superRefine` under sier det,
     * og skranken `ck_booking_bids__placed` i basen sier det samme.
     */
    draftItemId: z.uuid().nullish(),
    /**
     * PROGRAMPOSTEN PÅ EN KONSERT (QA G-9, 0424). En konsert har ikke noe brett, så budet peker
     * rett på artisten på konserten; dato og sted hentes fra konserten. Enten dette eller
     * `draftItemId`, aldri begge.
     */
    performanceId: z.uuid().nullish(),
    /**
     * SALEN I ET SPILLESTED (sak 230, 0455): plasseringen «sal + dato». Datoen er
     * `terms.performanceDate` (eller `performanceDate`) og er påkrevd med salen. Én plassering.
     */
    venueRoomId: z.uuid().nullish(),
    /**
     * «SEND BUD NÅ» I ÉN LAGRING (QA16 grensesnitt 4, funn 2): arrangementet budet gjelder. Salen og datoen
     * er arrangementets, og budet henger seg på innslaget der (artisten valgt i «Hvem opptrer?», ellers et
     * nytt innslag) i samme transaksjon. Lukkes fanen midt i, står ingenting halvt.
     */
    venueNightId: z.uuid().nullish(),
    /**
     * QA15 B4: BUDET LEGGER ARTISTEN I PROGRAMMET SELV. Festival, dag og scene: API-et lager raden i
     * programmet (årgangens eldste utkast, eller et nytt med `draftName`) og byr på den, i samme
     * transaksjon. Brukeren slipper å gå til Programmering først. Én plassering.
     */
    placeOn: z
      .object({
        editionId: z.uuid(),
        stageId: z.uuid(),
        day: z.iso.date(),
        draftName: z.string().trim().min(1).max(200).optional(),
      })
      .nullish(),
    /**
     * STØRRELSEN ARRANGØREN SPØR OM (§5.2, QA M3).
     *
     * «The format changes the fee, the travelling party and the rider» — så en forespørsel uten
     * den spør om noe artisten har tre priser på. Byrået fikk tre like rader og måtte gjette.
     */
    bookingArtistFormatId: z.uuid().nullish(),
    /**
     * STØRRELSEN VED NAVN, når arrangøren plukker den fra BYRÅETS liste (QA-punkt 6).
     *
     * -------------------------------------------------------------------------------------
     * Rapporten: «Det finnes ikke formatvalg i forespørselen, byråets svar eller budet.»
     *
     * Velgeren fantes, men leste festivalens EGNE formatrader — og dem har de nesten aldri.
     * Målt 12. september: 2 rader hos Demo Festivalorg mot 42 hos Standing Ovation. Lista var
     * tom, så kontrollen tegnet seg ikke.
     *
     * Størrelsene eies av byrået (§5.2), og festivalen leser dem gjennom porten fra 0151. Det
     * de får er NAVN — en id ville pekt inn i feil hus, som er den samme grunnen speilingen
     * matcher på navn (vedtak 95.1).
     *
     * `app.format_by_name` lager festivalens egen rad om den mangler. Enten-eller med id-en:
     * to felter der begge kunne vært satt ville reist spørsmålet om hvilket som vinner.
     */
    bookingArtistFormatName: z.string().trim().min(1).max(60).nullish(),
    terms: bidTermsInputSchema.optional(),
    /**
     * AVTALETYPEN, FRA KONSERTSIDEN (QA runde 1 av spillesteder, P1-2 og P3, 0430). Beløpet er
     * garantien (eller leien til arrangøren ved «leie»); prosenten gjelder versus og dørsplitt.
     * Godtas budet, blir de konsertens avtale. Uten type sier budet bare beløpet.
     */
    dealType: z.enum(CONCERT_DEAL_TYPES).nullish(),
    dealArtistPercent: z.number().min(0).max(100).nullish(),
    dealAfterCosts: z.boolean().nullish(),
    /**
     * AVTALEARKETS VILKÅR (C3, sak 274). Det budmodalen fyller ut, også feltene budraden ikke har
     * kolonner for (MVA, scene, aldersgrense, tillegg, reisefølge, standardvilkår, spesielle avtaler).
     * Blir versjonen på arket når budet sendes; `terms` over er det samme sett med budradens øyne.
     */
    dealTerms: dealTermsInputSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (
      (value.dealType === 'versus' || value.dealType === 'door_split') &&
      value.dealArtistPercent == null
    ) {
      ctx.addIssue({ code: 'custom', path: ['dealArtistPercent'], message: 'required_for_split' });
    }
    if (value.kind === 'bid' && (value.amountMinor === null || value.amountMinor === undefined)) {
      ctx.addIssue({ code: 'custom', path: ['amountMinor'], message: 'required_for_bid' });
    }
    /* QA15 B7: en dørsplitt har ingen garanti, så beløpet er 0. */
    const doorSplit =
      value.dealType === 'door_split' || value.dealTerms?.fee?.dealType === 'door_split';
    if (value.kind === 'bid' && value.amountMinor === 0 && !doorSplit) positiveAmountIssue(ctx);
    if (
      value.kind === 'interest' &&
      value.amountMinor !== null &&
      value.amountMinor !== undefined
    ) {
      ctx.addIssue({ code: 'custom', path: ['amountMinor'], message: 'not_allowed_for_interest' });
    }
    if (value.performanceId != null && value.draftItemId != null) {
      ctx.addIssue({ code: 'custom', path: ['performanceId'], message: 'one_placement_only' });
    }
    if (value.venueRoomId != null && (value.performanceId != null || value.draftItemId != null)) {
      ctx.addIssue({ code: 'custom', path: ['venueRoomId'], message: 'one_placement_only' });
    }
    if (
      value.placeOn != null &&
      (value.venueRoomId != null || value.performanceId != null || value.draftItemId != null)
    ) {
      ctx.addIssue({ code: 'custom', path: ['placeOn'], message: 'one_placement_only' });
    }
    /* Arrangementet ER plasseringen; salen kan stå ved siden av (den er arrangementets). */
    if (
      value.venueNightId != null &&
      (value.performanceId != null || value.draftItemId != null || value.placeOn != null)
    ) {
      ctx.addIssue({ code: 'custom', path: ['venueNightId'], message: 'one_placement_only' });
    }
    if (value.venueNightId != null && value.kind === 'interest') {
      ctx.addIssue({ code: 'custom', path: ['venueNightId'], message: 'not_allowed_for_interest' });
    }
  })
  .meta({ id: 'CreateBookingBidRequest' });
export type CreateBookingBidRequest = z.infer<typeof createBookingBidRequestSchema>;

/**
 * Å føre inn svaret man fikk, eller å rette et utkast.
 *
 * `status` og `responseNote` er det denne først og fremst brukes til: byrået ringte, og noen
 * skriver ned hva de sa. Beløpet kan rettes så lenge budet står som `draft` — etterpå er et nytt
 * tall et nytt bud, som er hele poenget med at historikken er rader.
 */
export const updateBookingBidRequestSchema = z
  .object({
    amountMinor: amountField.nullish(),
    respondsBy: z.iso.date().nullish(),
    status: bookingBidStatusSchema.optional(),
    responseNote: z.string().trim().min(1).max(MAX_BID_RESPONSE_NOTE).nullish(),
    notes: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
    terms: bidTermsInputSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.amountMinor === 0) positiveAmountIssue(ctx);
  })
  .meta({ id: 'UpdateBookingBidRequest' });
export type UpdateBookingBidRequest = z.infer<typeof updateBookingBidRequestSchema>;

/**
 * «GI NYTT BUD» ETTER ET MOTBUD (F22, Atle 04.10). Et nytt tall er et nytt bud — en ny rad på den
 * samme plassen, med vilkårene fra det gamle. Det gamle trekkes (`withdrawSuperseded`). Bare
 * beløpet, fristen og teksten til byrået er nye.
 */
export const rebidRequestSchema = z
  .object({
    amountMinor: amountField,
    respondsBy: z.iso.date().nullish(),
    message: z.string().trim().min(1).max(MAX_BID_MESSAGE).nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.amountMinor === 0) positiveAmountIssue(ctx);
  })
  .meta({ id: 'RebidRequest' });
export type RebidRequest = z.infer<typeof rebidRequestSchema>;

// ------------------------------------------------------- fra utkast til programpost

/**
 * Å skyve et utkast over i årgangens ekte program.
 *
 * ---------------------------------------------------------------------------------------------
 * DETTE ER ØYEBLIKKET SPEKULASJON BLIR AVTALE, og det er den viktigste hendelsen på siden.
 *
 * Atle, 3. september: «Når vi flytter dem over til festivalen med at et bud er akseptert får vi
 * overført navnelistene fra bookingbyrået.»
 *
 * ---------------------------------------------------------------------------------------------
 * PROGRAMPOSTEN FÅR ET NAVN, IKKE EN KONTAKT.
 *
 * En programpost trenger enten en tittel eller en artistkontakt, og her er det tittelen: artisten
 * har ingen kontakt, og skal ikke ha en ennå. Atle: «Det er ingen nødvendighet å ha kontakt på en
 * artist før advance begynner.» Kontakten oppstår når advancen starter, ikke her.
 *
 * ---------------------------------------------------------------------------------------------
 * `onlyConfirmed` ER PÅ SOM STANDARD, fordi det er det trygge. Et utkast inneholder som regel navn
 * man fortsatt vurderer, og å flytte hele brettet over ville lagt tolv «kanskje» inn i programmet
 * som ekte poster. Den som vil flytte alt, sier det.
 */
export const promoteDraftRequestSchema = z
  .object({
    /** Bare artister med status `confirmed`. Standard: ja. */
    onlyConfirmed: z.boolean().optional(),
    /**
     * Statusen programpostene får i bookingkjeden. Uten den avgjør plasseringen selv
     * (`promotionStatusFor`, QA13 60): et godtatt bud, eller en artist bekreftet for hånd uten bud i
     * Tvitvi, gir `verbally_confirmed`; alt annet `wishlist`. Aldri `signed` som standard — det ville løyet om en kontrakt som ikke finnes.
     */
    status: performanceStatusSchema.optional(),
    /**
     * KATEGORIEN KOSTNADENE LEGGES I, og uten den skrives ingen.
     *
     * Atle, sak 84: «Honoraret vet man jo, så det kan flyttes.» Men HVILKEN kategori det havner i
     * er en beslutning om organisasjonens kontoplan, ikke om koden — prinsipp 9. Så den velges av
     * den som trykker, fra sin egen liste, og uten et valg flyttes bare artistene.
     */
    costCategoryId: z.uuid().nullish(),
  })
  .meta({ id: 'PromoteDraftRequest' });
export type PromoteDraftRequest = z.infer<typeof promoteDraftRequestSchema>;

/** Hvorfor én plassering ikke ble flyttet. Alle tre er tilstander, ikke feil. */
export const PROMOTION_SKIP_REASONS = ['already_promoted', 'not_confirmed', 'no_stage'] as const;
export type PromotionSkipReason = (typeof PROMOTION_SKIP_REASONS)[number];

export const promoteDraftResponseSchema = z
  .object({
    /** Hva som ble opprettet, med den nye programpostens id, så klienten kan lenke rett dit. */
    created: z.array(
      z.object({ itemId: z.uuid(), performanceId: z.uuid(), artistName: z.string() }),
    ),
    /**
     * Hva som ble stående, og hvorfor. En kvittering som bare talte det den lyktes med ville
     * gjort «ni av tolv» til et tall ingen kan handle på.
     */
    skipped: z.array(
      z.object({
        itemId: z.uuid(),
        artistName: z.string(),
        reason: z.enum(PROMOTION_SKIP_REASONS),
      }),
    ),
    /** Hva de flyttede artistene koster til sammen. */
    totalMinor: z.number().int().nonnegative(),
    /**
     * Hvor mange budsjettlinjer som ble skrevet, og hva de kom til.
     *
     * Kan være LAVERE enn fire per artist, og det er hele poenget: reise, overnatting og mat
     * skrives bare der det aksepterte budet sier at arrangøren betaler dem. Honoraret skrives
     * alltid.
     */
    costLinesCreated: z.number().int().nonnegative(),
    costLinesMinor: z.number().int().nonnegative(),
    currency: z.string(),
  })
  .meta({ id: 'PromoteDraftResponse' });
export type PromoteDraftResponse = z.infer<typeof promoteDraftResponseSchema>;

// ------------------------------------------------- budet blir arrangement (sak 230, 0455)

/**
 * «LAG ARRANGEMENT» PÅ ET GODTATT BUD — det festivalens «Flytt over i programmet» gjør for én
 * plassering, med et arrangement i salen i stedet for en scene.
 *
 * Begge feltene er valgfrie: utelatt er budets egen sal og dato. Den som trykker kan flytte
 * arrangementet til en annen sal eller dag før det lages (Atle: «ved et tasteklikk»).
 */
export const bidToNightRequestSchema = z
  .object({
    venueRoomId: z.uuid().optional(),
    nightOn: z.iso.date().optional(),
  })
  .meta({ id: 'BidToNightRequest' });
export type BidToNightRequest = z.infer<typeof bidToNightRequestSchema>;

/**
 * «LEGG TIL OPPTREDENDE» på et arrangement uten programpost (sak 230): folk i salen uten
 * bookingbyrå. Programposten får navnet, og Personer-fanen tar resten.
 */
export const addNightPerformerRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
  })
  .meta({ id: 'AddNightPerformerRequest' });
export type AddNightPerformerRequest = z.infer<typeof addNightPerformerRequestSchema>;

/** Arrangementet og programposten på det — svaret på begge veiene inn. */
export const nightPerformanceResponseSchema = z
  .object({
    nightId: z.uuid(),
    /** Spillestedet, for adressen `/venue/$id/events/$nightId`. */
    festivalId: z.uuid(),
    performanceId: z.uuid(),
  })
  .meta({ id: 'NightPerformanceResponse' });
export type NightPerformanceResponse = z.infer<typeof nightPerformanceResponseSchema>;

// --------------------------------------------------------------- prishistorikken

/**
 * Hva en artist har kostet, år for år — Greenrooms «Pristrend over år».
 *
 * ---------------------------------------------------------------------------------------------
 * `year` ER ÅRET PRISEN GJELDER, ikke året den ble skrevet ned. En pris for 2027 kan avtales i
 * 2025, og det er 2027 grafen skal plassere den på.
 *
 * `confirmed` er per RAD og ikke per artist: en pris fra 2024 kan ha vært bekreftet selv om den
 * man har for 2027 er en gjetning, og omvendt. Det er nettopp den forskjellen en trendlinje må
 * kunne tegne — en strek gjennom fire bekreftede tall betyr noe annet enn en gjennom fire gjetninger.
 */
export const bookingPriceHistoryEntrySchema = z
  .object({
    id: z.uuid(),
    year: z.number().int(),
    amountMinor: z.number().int().nonnegative(),
    currency: z.string(),
    confirmed: z.boolean(),
    /** «byrået på telefon», «pressemelding», «vi gjettet». */
    source: z.string().nullable(),
    note: z.string().nullable(),
    /**
     * KILDEN, MASKINLESBART (#27, migrering 0270).
     *
     * «Bekreftet av Atle 19.09» og «Fra Standing Ovation 19.09» er to forskjellige setninger, og
     * `source` — en fritekst — kunne ikke skille dem. Skjermen velger setningen ut fra `sourceKind`
     * og fyller den med `sourceName`; API-et returnerer aldri en oversatt setning.
     */
    sourceKind: z.enum(['manual', 'agency', 'import']),
    sourceName: z.string().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    /**
     * FORMATET OG BUDSJETTLINJENE (1000, B5.6). For en rad lagt inn for hånd: det som ble skrevet.
     * For et godtatt bud: budets format. Null når ingen vet. Med standard, så et API som ikke er
     * startet på nytt ikke gir kontraktbrudd.
     */
    formatName: z.string().nullable().default(null),
    travelMinor: z.number().int().nonnegative().nullable().default(null),
    accommodationMinor: z.number().int().nonnegative().nullable().default(null),
    foodMinor: z.number().int().nonnegative().nullable().default(null),
    /** Raden kom fra et godtatt bud (kilden «Bud akseptert»). */
    fromBid: z.boolean().default(false),
    /** QA15 funn 14 (1210): prisen kom fra byråets svar på en forespørsel. */
    fromAnswer: z.boolean().default(false),
    /** Antall som reiser med, slik byrået svarte. */
    travellingParty: z.number().int().nonnegative().nullable().default(null),
  })
  .meta({ id: 'BookingPriceHistoryEntry' });
export type BookingPriceHistoryEntry = z.infer<typeof bookingPriceHistoryEntrySchema>;

/** Ikke paget: en artists historikk er noen få år, og grafen tegnes av hele den. */
export const bookingPriceHistoryResponseSchema = z
  .object({ items: z.array(bookingPriceHistoryEntrySchema) })
  .meta({ id: 'BookingPriceHistoryResponse' });
export type BookingPriceHistoryResponse = z.infer<typeof bookingPriceHistoryResponseSchema>;

/**
 * Byråhistorikken — hvem som representerte artisten, og når vi så det.
 *
 * Atle, 7. september: «Faktisk fint å ha denne historikken og på når artister bytter byrå, så ikke
 * slett det.»
 *
 * `fromAgencyName` er `null` når artisten ikke hadde byrå hos oss før; `toAgencyName` er `null`
 * når hen falt ut av en stall uten at vi fant henne igjen. Begge er ekte tilstander.
 *
 * `source` skiller en OBSERVASJON fra en BESLUTNING: «fant henne på Nordic Live sine sider» kan
 * være feil fordi en nettside er utdatert, «noen endret det her» kan ikke.
 */
export const bookingAgencyChangeSchema = z
  .object({
    id: z.uuid(),
    fromAgencyId: z.uuid().nullable(),
    fromAgencyName: z.string().nullable(),
    toAgencyId: z.uuid().nullable(),
    toAgencyName: z.string().nullable(),
    /** Datoen vi SÅ det, ikke datoen artisten signerte. */
    observedOn: z.iso.date(),
    source: z.enum(['agency_site', 'manual', 'import']),
    note: z.string().nullable(),
  })
  .meta({ id: 'BookingAgencyChange' });
export type BookingAgencyChange = z.infer<typeof bookingAgencyChangeSchema>;

/** Ikke paget: en artist bytter byrå noen få ganger i livet. */
export const bookingAgencyHistoryResponseSchema = z
  .object({ items: z.array(bookingAgencyChangeSchema) })
  .meta({ id: 'BookingAgencyHistoryResponse' });
export type BookingAgencyHistoryResponse = z.infer<typeof bookingAgencyHistoryResponseSchema>;

export const createPriceHistoryRequestSchema = z
  .object({
    year: z.number().int().min(1900).max(2200),
    amountMinor: amountField,
    currency: z.string().length(3).optional(),
    confirmed: z.boolean().optional(),
    source: z.string().trim().min(1).max(MAX_BOOKING_LABEL).nullish(),
    note: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
    /** «+ Legg til pris» (B5.6): formatet prisen gjaldt, og valgfrie budsjettlinjer. */
    formatName: z.string().trim().min(1).max(120).nullish(),
    travelMinor: amountField.nullish(),
    accommodationMinor: amountField.nullish(),
    foodMinor: amountField.nullish(),
  })
  .meta({ id: 'CreatePriceHistoryRequest' });
export type CreatePriceHistoryRequest = z.infer<typeof createPriceHistoryRequestSchema>;

/**
 * «BEKREFT PRIS» (#27c) — knappen som ligger ved prisen.
 *
 * ---------------------------------------------------------------------------------------------
 * Atle: «Prisen merkes estimert (~ og dempet farge) til den bekreftes: brukeren redigerer og
 * bekrefter, eller byrået sender pris (svar eller bud), som også bekrefter. Vis kilde og
 * tidspunkt.»
 *
 * DEN SKRIVER EN RAD I HISTORIKKEN, ikke et flagg på artisten. Det er hele forskjellen mellom
 * «siste bekreftede pris» og «siste skrevne»: et flagg har ett øyeblikk og ingen forgjenger, mens
 * en rad per år kan leses bakover — og #27 ber uttrykkelig om at pristrenden nederst merker hvert
 * punkt som estimert eller bekreftet.
 *
 * `feeMinor` er HONORARET og ikke totalen. Reise, opphold og mat er husets egne tall; byrået har
 * ikke sagt noe om dem, og en bekreftelse som svelget dem ville påstått at de også var avtalt.
 */
export const confirmArtistPriceRequestSchema = z
  .object({
    year: z.number().int().min(1900).max(2200),
    feeMinor: amountField,
    /**
     * REISE, OVERNATTING OG MAT — PER ARTIST, OG 0 ER ET SVAR (Atles svar 186).
     *
     * ------------------------------------------------------------------------------------------
     * De tre var husets satser ganget med reisefølget, og den ene måten å få dem ned på var
     * «Lokale steder»: en liste med stedsnavn som slo reisen til 0 og fjernet overnattingen for
     * alle som tilfeldigvis hadde skrevet hjembyen sin likt. Lista er borte (186), og det som
     * erstatter den er det Atle faktisk ba om: at den som BEKREFTER prisen skriver de tre tallene
     * selv, for denne artisten, i den forhandlingen — null kroner inkludert.
     *
     * DERFOR ER DE OPTIONAL OG IKKE NULLABLE. `undefined` er «la det stå som det er», `0` er «vi
     * kjører dem selv, det koster ingenting». De to kan ikke slås sammen uten at det blir umulig å
     * bekrefte en reise til null.
     *
     * Skrives på ARTISTEN og ikke på historikkraden: det er de kolonnene `COST_MINOR` summerer, og
     * derfor de som må endre seg for at totalen skal bli det som ble avtalt.
     */
    travelMinor: amountField.optional(),
    accommodationMinor: amountField.optional(),
    foodMinor: amountField.optional(),
    /**
     * «ARTIST DEKKER» (0440). Alle tre eller ingen: valget er ett svar om hva honoraret dekker,
     * og hurtigredigeringen sender det slik det står. `undefined` lar det som er lagret stå.
     */
    artistCovers: artistCoversSchema.optional(),
    currency: z.string().length(3).optional(),
    /**
     * Hvem som sier det. `manual` er standarden — en booker som trykker knappen. `agency` settes
     * av serveren når et svar eller et bud bekrefter, aldri av en klient som påstår det.
     */
    source: z.string().trim().min(1).max(MAX_BOOKING_LABEL).nullish(),
    note: z.string().trim().min(1).max(MAX_BOOKING_NOTE).nullish(),
  })
  .meta({ id: 'ConfirmArtistPriceRequest' });
export type ConfirmArtistPriceRequest = z.infer<typeof confirmArtistPriceRequestSchema>;

/**
 * «BRUK NY» / «BEHOLD MIN» på et felt kilden har flyttet på (K-6).
 *
 * `useSource` skriver kildens nye verdi inn på artisten og fjerner overstyringen — huset har gitt
 * kilden rett igjen. `keepMine` lar verdien stå og oppdaterer bare `sourceValue`, slik at varselet
 * går bort til NESTE gang kilden flytter seg. To ord, ikke en boolsk: «bruk ny = false» er ikke
 * det samme som «behold min» for den som leser koden om et halvt år.
 */
export const RESOLVE_OVERRIDE_CHOICES = ['useSource', 'keepMine'] as const;
export const resolveArtistOverrideRequestSchema = z
  .object({
    field: z.string().trim().min(1).max(60),
    choice: z.enum(RESOLVE_OVERRIDE_CHOICES),
  })
  .meta({ id: 'ResolveArtistOverrideRequest' });
export type ResolveArtistOverrideRequest = z.infer<typeof resolveArtistOverrideRequestSchema>;

// ------------------------------------------------------------------ satser for reise, mat og overnatting

/**
 * Husets satser og året det booker for (0072, 0999).
 *
 * Satsene × det FAKTISKE antallet som reiser fyller reise, overnatting og mat som budsjettlinjer,
 * og kan overskrives med faktisk kostnad (272, tillegget). Vektene for booking score er borte.
 */
export const bookingPricingSettingsSchema = z
  .object({
    travelPerPersonMinor: z.number().int().nonnegative(),
    accommodationPerPersonNightMinor: z.number().int().nonnegative(),
    foodPerPersonDayMinor: z.number().int().nonnegative(),
    /*
     * `localTravelPerPersonMinor` OG `localPlaces` STO HER TIL ATLES SVAR 186.
     *
     * «Fjern "Lokale steder" helt, sammen med satsen "Reise for lokale artister" og logikken som
     * bruker dem i kostnadsestimatet og i booking score.» Lista var kommaseparert tekst
     * sammenlignet tegn for tegn med artistens hjemby, og den sto på ORGANISASJONEN mens «lokalt»
     * avhenger av hvor festivalen er. Avstandsvarianten står i BACKLOG.md: den trenger koordinater
     * på både festivalen og artisten, og de finnes ikke.
     *
     * Reise, overnatting og mat blir stående på organisasjonen som før. Det som er nytt er at de
     * tre kan settes PER ARTIST når prisen bekreftes — se `confirmArtistPriceRequestSchema`.
     */
    /**
     * BYRÅETS STANDARDSATS, I BASISPUNKTER (vedtak 102.1, migrering 0165).
     *
     * Atle, 11. september: «Prosent per artist med en standardsats for huset, regnet av honoraret
     * alene. Satsen kan overstyres per artist.» 1500 er 15,00 %.
     *
     * Null er «ingen har sagt det», og da viser skjermen ingen provisjon i det hele tatt. En
     * standardverdi ville vært produktet som fant på en sats og regnet en årssum av den.
     */
    agencyCommissionBp: z.number().int().min(0).max(10_000).nullable(),
    currency: z.string(),
    defaultNights: z.number().int().min(0).max(30),
    /**
     * «VI BOOKER FOR ÅR ___» (272.1 a). Året bookingen og programmet jobber med. Lagret tomt betyr
     * inneværende år, og da sendes inneværende år her.
     */
    bookingYear: z.number().int().min(1900).max(2200),
  })
  .meta({ id: 'BookingPricingSettings' });
export type BookingPricingSettings = z.infer<typeof bookingPricingSettingsSchema>;

/** Én sats om gangen — skjermen lagrer det man endret, ikke hele skjemaet. */
export const updateBookingPricingSettingsRequestSchema = bookingPricingSettingsSchema
  .partial()
  .omit({ currency: true })
  .meta({ id: 'UpdateBookingPricingSettingsRequest' });
export type UpdateBookingPricingSettingsRequest = z.infer<
  typeof updateBookingPricingSettingsRequestSchema
>;
